/**
 * App Block: one App of an AppBase as a card on the edgeless canvas.
 * Opening the App and what else it offers is up to the host, which
 * provides `AppBlockConfigExtension`.
 */
import { I18n } from '@affine/i18n';
import { MoreHorizontalIcon, PageIcon } from '@blocksuite/icons/lit';
import { BlockComponent, toGfxBlockComponent } from '@blocksuite/std';
import { GfxViewInteractionExtension } from '@blocksuite/std/gfx';
import { html, nothing } from 'lit';

import type { AppBlockModel } from '../schemas/app-model.js';
import { AppBlockSchema } from '../schemas/app-model.js';
import { AppBlockConfigExtension } from './app-block-config.js';
import * as styles from './app-canvas-block.css.js';

export const AFFINE_APP_CANVAS = 'affine-app-canvas';

/** Base of the canvas view; the block lives on canvases only. */
export class AppBlockComponent extends BlockComponent<AppBlockModel> {
  override renderBlock() {
    return nothing;
  }
}

export class AppCanvasBlockComponent extends toGfxBlockComponent(
  AppBlockComponent
) {
  private get _config() {
    return this.std.getOptional(AppBlockConfigExtension.identifier);
  }

  private get _openApp() {
    return this._config?.openApp;
  }

  private readonly _open = (event: Event) => {
    const openApp = this._openApp;
    if (!openApp) return;
    event.stopPropagation();
    openApp(this.model);
  };

  private get _actionsAnchor() {
    return this.querySelector<HTMLElement>(`.${styles.more}`);
  }

  /** The actions of the host where it offers them, opening the App else. */
  private readonly _activate = (event: Event) => {
    const showActions = this._config?.showActions;
    const anchor = this._actionsAnchor;
    if (!showActions || !anchor) {
      this._open(event);
      return;
    }
    event.stopPropagation();
    // Keeps Space from scrolling and Enter from a second, synthetic click.
    event.preventDefault();
    showActions(this.model, anchor);
  };

  override renderGfxBlock() {
    const { title, routePath, isFrame, trashed } = this.model.props;
    const hasActions = Boolean(this._config?.showActions);
    const interactive = hasActions || Boolean(this._openApp);
    const actionsLabel = I18n.t(
      'com.streamlain.appBases.studio.apps.actions.label',
      { title }
    );
    return html`
      <div
        class=${styles.card}
        role=${interactive ? 'button' : nothing}
        tabindex=${interactive ? '0' : nothing}
        aria-haspopup=${hasActions ? 'menu' : nothing}
        data-openable=${interactive}
        data-actions=${hasActions}
        data-trashed=${trashed}
        title=${trashed
          ? I18n.t('com.streamlain.appBases.studio.apps.trashHint')
          : nothing}
        data-testid="app-block"
        @click=${this._activate}
        @keydown=${(event: KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') this._activate(event);
        }}
      >
        <span class=${styles.head}>
          <span class=${styles.icon}>${PageIcon()}</span>
          <span class=${styles.title}>${title}</span>
        </span>
        <span class=${styles.meta}>
          <span class=${styles.route}>${routePath}</span>
          ${trashed
            ? html`<span class=${styles.badge}
                >${I18n.t(
                  'com.streamlain.appBases.studio.apps.trashBadge'
                )}</span
              >`
            : nothing}
          ${isFrame
            ? html`<span class=${styles.badge}
                >${I18n.t(
                  'com.streamlain.appBases.studio.apps.frameBadge'
                )}</span
              >`
            : nothing}
        </span>
      </div>
      ${hasActions
        ? html`<button
            type="button"
            class=${styles.more}
            aria-label=${actionsLabel}
            aria-haspopup="menu"
            title=${actionsLabel}
            data-testid="app-block-actions"
            @click=${this._activate}
          >
            ${MoreHorizontalIcon()}
          </button>`
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [AFFINE_APP_CANVAS]: AppCanvasBlockComponent;
  }
}

/** The card keeps the size of its content: no resize, no rotation. */
export const AppBlockInteraction =
  GfxViewInteractionExtension<AppCanvasBlockComponent>(
    AppBlockSchema.model.flavour,
    {
      resizeConstraint: { allowedHandlers: [] },
      // A readonly overview pans on drag instead of picking up a card.
      handleSelection: () => ({
        selectable(context) {
          return !context.model.store.readonly && context.default(context);
        },
      }),
      handleRotate: () => ({
        beforeRotate(context) {
          context.set({ rotatable: false });
        },
      }),
    }
  );
